// The commits column of the Files tab: the branch picker, then the commits newest first (or only
// those that changed one file), read a page at a time as the list scrolls.
import type { GitRef } from '@modelviewer/core';
import { GitBranch, GitMerge, History, Loader2, X } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { cn } from '../lib/cn';
import { loadMoreCommits, refreshRefs, selectCommit, setStart, showHistory, useFiles } from '../state/files';
import { ago, refLabel, shortId, subject, when } from './format';

function BranchPicker() {
  const refs = useFiles((s) => s.refs);
  const head = useFiles((s) => s.head);
  const start = useFiles((s) => s.start);
  const groups: [string, GitRef[]][] = [
    ['Branches', refs.filter((r) => r.name.startsWith('refs/heads/'))],
    ['Remote branches', refs.filter((r) => r.name.startsWith('refs/remotes/'))],
    ['Tags', refs.filter((r) => r.name.startsWith('refs/tags/'))],
  ];
  const checkedOut = head?.ref ? refLabel(head.ref) : head?.id ? `${shortId(head.id)} (detached)` : 'no commits';
  return (
    <label className="flex min-w-0 flex-1 items-center gap-1.5 rounded-md border border-border px-2 hover:border-border-strong" title="The branch or tag whose commits are listed">
      <GitBranch size={13} className="flex-none text-faint" />
      <select
        value={start}
        onChange={(e) => void setStart(e.target.value)}
        onFocus={() => void refreshRefs()}
        aria-label="Branch"
        className="h-7 min-w-0 flex-1 cursor-pointer bg-transparent text-[12.5px] text-text outline-none pointer-coarse:h-9"
      >
        <option value="HEAD">{checkedOut} (checked out)</option>
        {groups.map(([label, list]) =>
          list.length ? (
            <optgroup key={label} label={label}>
              {list.map((r) => (
                <option key={r.name} value={r.name}>
                  {refLabel(r.name)}
                </option>
              ))}
            </optgroup>
          ) : null,
        )}
      </select>
    </label>
  );
}

export function CommitList({ onPick }: { onPick?: () => void }) {
  const commits = useFiles((s) => s.commits);
  const selected = useFiles((s) => s.commit);
  const complete = useFiles((s) => s.complete);
  const loading = useFiles((s) => s.loading);
  const history = useFiles((s) => s.history);
  const scanned = useFiles((s) => s.scanned);
  const end = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLDivElement>(null);

  // Read the next page when the end of the list comes into view.
  useEffect(() => {
    const target = end.current;
    if (!target) return;
    const observer = new IntersectionObserver((entries) => entries.some((e) => e.isIntersecting) && loadMoreCommits(), { root: list.current, rootMargin: '400px' });
    observer.observe(target);
    return () => observer.disconnect();
  }, [commits.length, complete]);

  useEffect(() => {
    list.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [selected]);

  const pick = (id: string) => {
    void selectCommit(id);
    onPick?.();
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex flex-none items-center gap-2 border-b border-border px-2 py-1.5">
        <BranchPicker />
      </div>
      {history && (
        <div className="flex flex-none items-center gap-1.5 border-b border-border bg-accent-soft px-2.5 py-1.5 text-[12px] text-accent" data-testid="history-filter">
          <History size={13} className="flex-none" />
          <span className="min-w-0 flex-1 truncate" title={history}>
            Changes to <span className="font-medium">{history.slice(history.lastIndexOf('/') + 1)}</span>
          </span>
          <button
            type="button"
            onClick={() => void showHistory(null)}
            className="flex h-5 w-5 flex-none items-center justify-center rounded hover:bg-active pointer-coarse:h-8 pointer-coarse:w-8"
            title="Show every commit again"
            aria-label="Show every commit again"
          >
            <X size={13} />
          </button>
        </div>
      )}
      <div
        ref={list}
        role="listbox"
        aria-label="Commits"
        tabIndex={0}
        className="min-h-0 flex-1 overflow-y-auto py-1 outline-none focus-visible:ring-1 focus-visible:ring-accent focus-visible:ring-inset"
        onKeyDown={(e) => {
          const at = commits.findIndex((c) => c.id === selected);
          const to = { ArrowUp: at - 1, ArrowDown: at + 1, Home: 0, End: commits.length - 1 }[e.key];
          if (to === undefined) return;
          e.preventDefault();
          const next = commits[Math.max(0, Math.min(commits.length - 1, to))];
          if (next) void selectCommit(next.id);
        }}
      >
        {commits.map((c) => (
          <div
            key={c.id}
            role="option"
            aria-selected={c.id === selected}
            onClick={() => pick(c.id)}
            title={`${subject(c)}\n${c.author.name}, ${when(c.author)}\n${c.id}`}
            className={cn(
              'mx-1.5 cursor-default rounded-md px-2 py-1.5 select-none pointer-coarse:py-2.5',
              c.id === selected ? 'bg-active text-text' : 'text-muted hover:bg-hover hover:text-text',
            )}
          >
            <div className="flex min-w-0 items-center gap-1.5">
              {c.parents.length > 1 && <GitMerge size={12} className="flex-none text-faint" aria-label="Merge" />}
              <span className={cn('min-w-0 flex-1 truncate text-[12.5px]', c.id === selected ? 'text-text' : 'text-text/90')}>{subject(c)}</span>
            </div>
            <div className="mt-0.5 flex min-w-0 items-center gap-1 text-[11px] text-faint">
              <span className="min-w-0 truncate">{c.author.name}</span>
              <span className="flex-none">·</span>
              <span className="flex-none">{ago(c.author)}</span>
              <span className="ml-auto flex-none font-mono">{shortId(c.id)}</span>
            </div>
          </div>
        ))}
        <div ref={end} className="px-3.5 py-2 text-[11.5px] text-faint">
          {loading ? (
            <span className="flex items-center gap-1.5">
              <Loader2 size={12} className="animate-spin" /> {history && scanned ? `Looked through ${scanned.toLocaleString()} commits…` : 'Reading commits…'}
            </span>
          ) : complete ? (
            commits.length ? (
              history ? `${commits.length} commit${commits.length === 1 ? '' : 's'} changed ${history.slice(history.lastIndexOf('/') + 1)}` : `${commits.length.toLocaleString()} commit${commits.length === 1 ? '' : 's'}`
            ) : history ? (
              `No commit changed ${history.slice(history.lastIndexOf('/') + 1)}.`
            ) : (
              'No commits yet.'
            )
          ) : null}
        </div>
      </div>
    </div>
  );
}
